'use client';

import { create } from 'zustand';
import { useOverlayStore } from '@/shared/components/overlay/overlay.store';

export const AUTH_GATE_MODAL_KEY = 'auth-gate';

export type AuthGateView = 'login' | 'register' | 'verifySent';
export type AuthGateOrigin = 'web' | 'client';

export interface AuthGateOptions {
  view?: AuthGateView;
  prefillEmail?: string;
  hideSocial?: boolean;
  origin?: AuthGateOrigin;
  pendingAction?: () => void;
  redirectUrl?: string;
}

const currentLocation = () => {
  if (typeof window === 'undefined') return '';
  return `${window.location.pathname}${window.location.search}`;
};

interface AuthGateState extends Required<Pick<AuthGateOptions, 'view' | 'hideSocial' | 'origin'>> {
  prefillEmail: string;
  redirectUrl: string;
  pendingAction: (() => void) | null;
  setView: (view: AuthGateView) => void;
  setPrefillEmail: (email: string) => void;
  reset: () => void;
}

const initialState = {
  view: 'login' as AuthGateView,
  prefillEmail: '',
  hideSocial: false,
  origin: 'web' as AuthGateOrigin,
  redirectUrl: '',
  pendingAction: null,
};

export const useAuthGateStore = create<AuthGateState>((set) => ({
  ...initialState,
  setView: (view) => set({ view }),
  setPrefillEmail: (prefillEmail) => set({ prefillEmail }),
  reset: () => set(initialState),
}));

export const openAuthGate = ({
  view = 'login',
  prefillEmail = '',
  hideSocial = false,
  origin = 'web',
  redirectUrl,
  pendingAction,
}: AuthGateOptions = {}) => {
  useAuthGateStore.setState({
    view,
    prefillEmail,
    hideSocial,
    origin,
    redirectUrl: redirectUrl || currentLocation(),
    pendingAction: pendingAction ?? null,
  });
  useOverlayStore.getState().showOverlay(AUTH_GATE_MODAL_KEY);
};

export const closeAuthGate = () => {
  const { activeModalKey, hideOverlay } = useOverlayStore.getState();

  if (activeModalKey === AUTH_GATE_MODAL_KEY) {
    hideOverlay();
  }

  useAuthGateStore.getState().reset();
};
